import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { UseMutationResult } from '@tanstack/react-query';
import {
  Alert,
  Button,
  ButtonProps,
  Descriptions,
  Form,
  FormItemProps,
  FormProps,
  ResultProps,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { AxiosResponse } from 'axios';

import { CreateProfileDto, EditProfileDto, ProfileWithDetails } from '@app/domain-t3-interfaces';
import { Azure } from '@app/web-components';
import { Bundles } from '@app/web-pages';

import { Projects } from '../../admin';
import { useGetWorkerEligibility } from '../data';

export type ProfileFormData = {
  worker?: {
    label: string;
    value: string;
  };
  node?: {
    label: string;
    value: number;
  };
  project?: {
    label: string;
    value: number;
  };
};

export const useProfileForm = (
  mutate:
    | UseMutationResult<AxiosResponse<ProfileWithDetails, unknown>, unknown, CreateProfileDto, unknown>
    | UseMutationResult<AxiosResponse<ProfileWithDetails, unknown>, unknown, EditProfileDto, unknown>,
  initialValues?: ProfileFormData,
) => {
  const [form] = Form.useForm<ProfileFormData>();
  const worker = Form.useWatch('worker', form) ?? initialValues?.worker;
  const node = Form.useWatch('node', form) ?? initialValues?.node;
  const eligibilityQuery = useGetWorkerEligibility(worker?.value, { enabled: !!worker?.value });
  const navigate = useNavigate();

  const { getSelectProps: getProjectSelectProps, validateSelectedProjects } = Projects.useProjectPicker({
    labelInValue: true,
    eligibleNodes: node ? [node.value] : [],
    value: form.getFieldValue('project'),
  });

  const getCreateAnotherButtonProps = (): ButtonProps => ({
    children: 'Create Another',
    onClick: () => {
      mutate.reset();
      form.resetFields();
    },
  });

  const getWorkerFormItemProps = (): FormItemProps => ({
    children: <Azure.WorkerPicker placeholder="Search Intel Workers" />,
    label: 'Worker',
    name: 'worker',
    required: true,
    rules: [
      { required: true },
      {
        validator: (rule: unknown, value: ProfileFormData['worker']) =>
          new Promise((resolve, reject) => {
            if (!value) return resolve('');
            if (eligibilityQuery.isError) return reject('Error fetching eligibility');
            if (eligibilityQuery.isLoading || eligibilityQuery.isFetching) return resolve('');
            if (eligibilityQuery.data?.eligible) return resolve('');
            return reject('Worker not eligible');
          }),
      },
    ],
  });

  const getNodeFormItemProps = (): FormItemProps => ({
    children: (
      <Select
        options={eligibilityQuery.data?.nodes.map((node) => ({ label: node.name, value: node.id }))}
        loading={eligibilityQuery.isFetching || eligibilityQuery.isPending}
        disabled={!eligibilityQuery.data?.nodes?.length}
        labelInValue
      />
    ),
    label: 'Node',
    name: 'node',
    required: true,
    rules: [{ required: true }],
  });

  const getProjectFormItemProps = (): FormItemProps => ({
    children: <Select {...getProjectSelectProps({ placeholder: 'Select Project', disabled: !node })} />,
    label: 'Project',
    name: 'project',
    dependencies: ['node'],
    required: true,
    rules: [
      { required: true },
      {
        validator: (rule: unknown, value: ProfileFormData['project']) =>
          new Promise((resolve, reject) => {
            const isValid = validateSelectedProjects(value);
            if (isValid) {
              resolve('');
            } else {
              reject('Selected project is invalid.');
            }
          }),
      },
    ],
  });

  const getResetButtonProps = (): ButtonProps => ({
    children: 'Reset',
    onClick: () => form.resetFields(),
  });

  const getSaveButtonProps = (): ButtonProps => ({
    children: 'Save',
    htmlType: 'submit',
    disabled: !eligibilityQuery.data?.eligible,
    loading: mutate.isPending,
    type: 'primary',
  });

  const getCancelButtonProps = (): ButtonProps => ({
    children: 'Cancel',
    onClick: () => navigate('../'),
  });

  const _getDescriptionItems = () => [
    { key: 'worker', label: 'Worker', children: worker?.label },
    { key: 'node', label: 'Node', children: node?.label },
  ];

  const _onValuesChange = (changedValues) => {
    if ('worker' in changedValues) {
      form.resetFields(['node', 'project']);
    }
    if ('node' in changedValues) {
      form.resetFields(['project']);
    }
  };

  const getCreateFormProps = (): FormProps<ProfileFormData> & { children: ReactNode } => ({
    children: (
      <>
        <Form.Item {...getWorkerFormItemProps()} />
        <Form.Item label="Eligibility">
          {eligibilityQuery.isFetching || eligibilityQuery.isLoading ? (
            <Spin />
          ) : eligibilityQuery.isError ? (
            <Alert message="Error fetching eligibility" type="error" />
          ) : eligibilityQuery.data ? (
            <Bundles.EligibilityRenderer
              isEligible={eligibilityQuery.data?.eligible}
              eligibility={eligibilityQuery.data?.messages ?? []}
            />
          ) : (
            <Typography.Text type="secondary">Select a worker to get eligibility</Typography.Text>
          )}
        </Form.Item>
        <Form.Item {...getNodeFormItemProps()}></Form.Item>
        <Form.Item {...getProjectFormItemProps()} />
        <Space>
          <Button {...getSaveButtonProps()} />
          <Button {...getResetButtonProps()} />
          <Button {...getCancelButtonProps()} />
        </Space>
      </>
    ),
    form,
    layout: 'vertical',
    onFinish: (formData) => {
      if (!formData.project || !formData.worker) return;
      const createDto: CreateProfileDto = {
        projectId: formData.project.value,
        enterpriseId: formData.worker.value,
      };
      mutate.mutate(createDto);
    },
    onValuesChange: _onValuesChange,
  });

  const getEditFormProps = (): FormProps<ProfileFormData> & { children: ReactNode } => ({
    children: (
      <>
        <Form.Item dependencies={['supplierOption', 'supplierName', 'node']} preserve>
          {() => <Descriptions column={1} bordered items={_getDescriptionItems()} />}
        </Form.Item>
        <Form.Item {...getProjectFormItemProps()} />
        <Space>
          <Button {...getSaveButtonProps()} />
          <Button {...getResetButtonProps()} />
          <Button {...getCancelButtonProps()} />
        </Space>
      </>
    ),
    form,
    layout: 'vertical',
    onFinish: (formData) => {
      if (!formData.project) return;
      const editDto: EditProfileDto = {
        projectId: formData.project.value,
      };
      // Cannot do a type assertion to mutate here because complex type assertions are not supported at runtime by JS
      mutate.mutate(editDto as CreateProfileDto);
    },
    onValuesChange: _onValuesChange,
  });

  const getGotoProfilesButtonProps = (): ButtonProps => ({
    children: 'Go to Profiles',
    type: 'primary',
    onClick: () => navigate('../'),
  });

  const getSuccessResultProps = (props?: ResultProps): ResultProps => ({
    status: 'success',
    subTitle: `Profile added for ${mutate.data?.data.user.name} on contract ${mutate.data?.data.supplierContract.contractName}`,
    ...props,
    extra: [
      <Button {...getGotoProfilesButtonProps()} />,
      <Button {...getCreateAnotherButtonProps()} />,
      ...React.Children.toArray(props?.extra),
    ],
  });

  return { form, getCreateFormProps, getEditFormProps, getCreateAnotherButtonProps, getSuccessResultProps };
};
